import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import userApi from "../services/userApi";
import { setToken, removeToken, getToken } from "../services/auth";

interface UserProfile {
  id: string;
  fullname: string;
  email: string;
  nickName: string;
  status: number;
  role: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
}

const initialState: AuthState = {
  isAuthenticated: !!getToken(), // restore from localStorage
  user: null,
  loading: false,
};

// 🔹 Login thunk
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    credentials: { email: string; password: string; deviceID: string; isFormLogin: boolean },
    thunkAPI
  ) => {
    try {
      const responseLogin = await userApi.login(credentials);
      if (!responseLogin.data.accessToken) {
        return thunkAPI.rejectWithValue("No token returned from login.");
      }

      // Save token immediately
      setToken(responseLogin.data.accessToken);

      // Fetch user profile
      const userDetails = await userApi.getUser();
      return userDetails;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || "Login failed"
      );
    }
  }
);

// 🔹 Restore session on refresh
export const fetchUserProfile = createAsyncThunk(
  "auth/fetchUserProfile",
  async (_, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) {
        return thunkAPI.rejectWithValue("No token found.");
      }
      const userDetails = await userApi.getUser();
      return userDetails;
    } catch (error: any) {
      return thunkAPI.rejectWithValue("Failed to fetch user profile.");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      removeToken();
      state.isAuthenticated = false;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔹 loginUser
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<UserProfile>) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(loginUser.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.loading = false;
      })

      // 🔹 fetchUserProfile
      .addCase(fetchUserProfile.fulfilled, (state, action: PayloadAction<UserProfile>) => {
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
